import { useEffect, useState } from "react";
import { createClass, getAllClasses, updateClass } from "../api/student/studentApi";
import { BookOpen, Plus, Edit2 } from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Modal,
  Input,
  Alert,
  Skeleton,
} from "../components/ui";

const ClassList = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [form, setForm] = useState({ className: "", classCode: "", description: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit modal
  const [editClass, setEditClass] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const loadClasses = async () => {
    try {
      const data = await getAllClasses();
      setClasses(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClasses(); }, []);

  /* ── CREATE ── */

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreateLoading(true);
      setCreateError("");
      await createClass(form);
      setForm({ className: "", classCode: "", description: "" });
      await loadClasses();
    } catch (err) {
      setCreateError(err?.response?.data?.message || "Failed to create class");
    } finally {
      setCreateLoading(false);
    }
  };

  /* ── EDIT ── */

  const openEdit = (cls) => {
    setEditClass(cls);
    setEditForm({
      className: cls.className,
      classCode: cls.classCode,
      description: cls.description || "",
      active: cls.active,
    });
    setEditError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      setEditError("");
      await updateClass(editClass._id, editForm);
      setEditClass(null);
      await loadClasses();
    } catch (err) {
      setEditError(err?.response?.data?.message || "Failed to update class");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Class Management
          </h1>
          <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
            Configure academic classes, standard codes, and status visibility.
          </p>
        </div>
      </div>

      {/* Create Form Container */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
            Add New Class
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4 sm:p-5">
          {createError && (
            <Alert variant="danger" className="mb-4">
              {createError}
            </Alert>
          )}

          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                label="Class Name"
                type="text"
                value={form.className}
                onChange={(e) => setForm({ ...form, className: e.target.value })}
                placeholder="e.g. 1, 2, 12"
                required
              />

              <Input
                label="Class Code"
                type="text"
                value={form.classCode}
                onChange={(e) => setForm({ ...form, classCode: e.target.value })}
                placeholder="e.g. C1, C12"
                required
              />

              <Input
                label="Description"
                type="text"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional notes"
              />
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                isLoading={createLoading}
                className="text-xs font-semibold px-5"
              >
                Create Class
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Classes Table Container */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none">
        <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-3">
          <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
            All Classes
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              <Skeleton height={20} width="30%" />
              <Skeleton height={140} width="100%" />
            </div>
          ) : classes.length === 0 ? (
            <div className="p-8 text-center text-xs text-neutral-400 dark:text-neutral-500">
              No classes found. Add your first class above.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 text-neutral-400 dark:text-neutral-500 uppercase tracking-wider font-semibold">
                    <th className="py-2.5 px-4 font-medium">Class Name</th>
                    <th className="py-2.5 px-4 font-medium">Class Code</th>
                    <th className="py-2.5 px-4 font-medium">Description</th>
                    <th className="py-2.5 px-4 font-medium">Status</th>
                    <th className="py-2.5 px-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200">
                  {classes.map((cls) => (
                    <tr key={cls._id} className="hover:bg-neutral-50/60 transition-colors">
                      <td className="py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">
                        Class {cls.className}
                      </td>
                      <td className="py-3 px-4 font-mono text-neutral-600 dark:text-neutral-300">
                        {cls.classCode}
                      </td>
                      <td className="py-3 px-4 text-neutral-500 dark:text-neutral-400">
                        {cls.description || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={cls.active ? "success" : "danger"}
                          className="text-[10px] px-2 py-0.5 font-medium"
                        >
                          {cls.active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          leftIcon={<Edit2 className="h-3 w-3" />}
                          onClick={() => openEdit(cls)}
                          className="text-xs h-7 px-2.5 border-neutral-200 dark:border-neutral-800"
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Class Modal */}
      {editClass && (
        <Modal
          isOpen={!!editClass}
          onClose={() => setEditClass(null)}
          title="Edit Class Information"
          className="w-full max-w-sm"
        >
          <div className="space-y-3.5">
            {editError && (
              <Alert variant="danger">
                {editError}
              </Alert>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-3">
              <Input
                label="Class Name"
                type="text"
                value={editForm.className}
                onChange={(e) => setEditForm({ ...editForm, className: e.target.value })}
                required
              />

              <Input
                label="Class Code"
                type="text"
                value={editForm.classCode}
                onChange={(e) => setEditForm({ ...editForm, classCode: e.target.value })}
                required
              />

              <Input
                label="Description"
                type="text"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />

              <div>
                <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                  Status
                </label>
                <select
                  value={editForm.active ? "true" : "false"}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.value === "true" })}
                  className="w-full rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-3 py-2 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditClass(null)}
                  className="flex-1 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={editLoading}
                  className="flex-1 text-xs"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ClassList;